/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	Avatar,
	Badge,
	Button,
	Card,
	createTableColumn,
	DataGrid,
	DataGridBody,
	DataGridCell,
	DataGridHeader,
	DataGridHeaderCell,
	DataGridRow,
	Link,
	makeStyles,
	Spinner,
	TableCellLayout,
	TableColumnDefinition,
	Text,
	tokens
} from '@fluentui/react-components';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { ActivityId, FirstColumn, Item, Status } from './types';
import { useAutomationRuleActivity } from './activity-provider';
import ListItemSkeleton from './list-item-skeleton';
import { useSearchParams } from 'react-router-dom';
import EmptyActivity from 'src/components/shared/empty-activity';

const TableHeaderTitle = (props: { children?: ReactNode }) => {
	const classes = useStyles();
	return (
		<Text
			as='span'
			className={classes.tableHeaderTitle}
		>
			{props?.children}
		</Text>
	);
};

const RenderFirstColumn = (props: FirstColumn) => {
	const classes = useStyles();

	const parseToSec = useCallback(() => {
		return (props.timeTaken / 1000).toFixed(2);
	}, []);

	const formatDate = useCallback(() => {
		const userLocale = Intl.DateTimeFormat().resolvedOptions().locale; 
		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const options: any = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: userTimeZone };
		const date = new Date(props.executedOnTime);
		const formatted = date.toLocaleString(userLocale, options);
		return `${formatted}`;
	}, []);

	return (
		<TableCellLayout style={{ width: '100%' }}>
			<span className={classes.firstColumnWrapper}>
				<Text className={classes.firstColumnTitle}>{props.name}</Text>
				<span className={classes.firstColumnBottom}>
					<Text className={classes.firstColumnLabel}>Executed at: </Text>
					<Text className={classes.firstColumnValue}>{formatDate()}</Text>
					{' '}
					<Text className={classes.firstColumnLabel}>Execution time: </Text>
					<Text className={classes.firstColumnValue}>{parseToSec()}s</Text>
				</span>
			</span>
		</TableCellLayout>
	);
};

const RenderWiId = (props: ActivityId) => {
	return (
		<Link
			href={props.url}
			target="_blank"
			rel="noopener noreferrer"
			onClick={(ev) => {
				ev.stopPropagation();
			}}
		>
			#{props.label}
		</Link>
	);
};

const RenderStatus = (props: Status) => {
	if (props.label === 'success') {
		return <Badge appearance="filled" color="success">Success</Badge>;
	}

	return <Badge appearance="tint" color="severe">Error</Badge>;
};

const RenderAvatar = (props: { label: string; url?: string }) => {
	const classes = useStyles();

	return (
		<TableCellLayout>
			<div className={classes.authorCellContent}>
				<Avatar name={props.label} image={{ src: props.url }} />
				<Text className={classes.authorCellText}>{props.label}</Text>
			</div>
		</TableCellLayout>
	);
};

const columns: TableColumnDefinition<Item>[] = [
	createTableColumn<Item>({
		columnId: 'firstColumn',
		renderHeaderCell: () => <TableHeaderTitle>Automation rule</TableHeaderTitle>,
		renderCell: (item) => <RenderFirstColumn {...item.firstColumn} />
	}),
	createTableColumn<Item>({
		columnId: 'id',
		renderHeaderCell: () => <TableHeaderTitle>Trigger work item</TableHeaderTitle>,
		renderCell: (item) => (<RenderWiId {...item.id} />)
	}),
	createTableColumn<Item>({
		columnId: 'status',
		renderHeaderCell: () => <TableHeaderTitle>Status</TableHeaderTitle>,
		renderCell: (item) => (
			<TableCellLayout>
				<RenderStatus {...item.status} />
			</TableCellLayout>
		)
	}),
	createTableColumn<Item>({
		columnId: 'executedBy',
		renderHeaderCell: () => <TableHeaderTitle>Executed By</TableHeaderTitle>,
		renderCell: (item) => (
			<TableCellLayout>
				<RenderAvatar
					label={item.executedBy.name}
					url={item.executedBy.avatar}
				/>
			</TableCellLayout>
		)
	}),
];

export default function DataGridContent() {
	const classes = useStyles();
	const [searchParams] = useSearchParams();
	const [loadingMore, setLoadingMore] = useState(false);
	const {
		list,
		loadInitialActivityList,
		loadingList,
		openDetailsHandler,
		getListByFilter,
		lastActivityId,
		filter
	} = useAutomationRuleActivity();

	const init = useRef(true);

	const isEmpty = useMemo(() => {
		return (!loadingList && list.length === 0);
	}, [list, loadingList]);

	useEffect(() => {
		if (init.current) {
			init.current = false;
			loadInitialActivityList();
		}
	}, []);

	return (
		<div className={classes.root}>
			<Card className={classes.tableRoot}>
				<DataGrid
					items={list}
					columns={columns}
					size='medium'
				>
					{!isEmpty && ( 
						<DataGridHeader>
							<div className={classes.tableHeaderTop}>
								<span>
									<Text className={classes.totalRules}>Total</Text>
									<Badge color="informative" size="large">{list.length}</Badge>
								</span>
							</div>
							<DataGridRow className={classes.tableHeaderRow}>
								{({ renderHeaderCell, columnId }) => (
									<DataGridHeaderCell
										className={columnId === 'firstColumn' ? classes.firstColumnRoot : undefined}
									>
										{renderHeaderCell()}
									</DataGridHeaderCell>
								)}
							</DataGridRow>
						</DataGridHeader>
					)}
					{
						loadingList && (
							<>
								<ListItemSkeleton />
								<ListItemSkeleton />
								<ListItemSkeleton />
							</>
						)
					}
					{isEmpty && (
						<EmptyActivity
							title="No activity"
							description="Here you will see all activity related to this automation rule/project"
						/>
					)}
					{!loadingList && (
						<DataGridBody<Item>>
							{({item, rowId}) => (
								<DataGridRow
									role="link"
									key={rowId}
									className={classes.tableRow}
									onClick={() => {
										openDetailsHandler(item.activityData.id);
									}}
								>
									{({renderCell, columnId}) => (
										<DataGridCell
											
											className={columnId === 'firstColumn' ? classes.firstColumnRoot : undefined}
											key={columnId}
										>
											{renderCell(item)}
										</DataGridCell>
									)}
								</DataGridRow>
							)}
						</DataGridBody>
					)}
				</DataGrid>
				<div style={{width: '100%', display: 'flex', justifyContent: 'center', padding: 12}}>
					<Button
						appearance="subtle"
						disabled={loadingList || loadingMore}
						icon={loadingMore ? <Spinner size="extra-tiny" /> : null}
						onClick={async () => {
							const automationRuleId = searchParams.get('id') as string;
							setLoadingMore(true);
							await getListByFilter({
								oldActivityList: list,
								automationRuleId: automationRuleId,
								lastActivityId: lastActivityId,
								lastTimeFilter: filter.time
							});
							setLoadingMore(false);
						}}
					>
						Load more
					</Button>
				</div>
			</Card>
		</div>
	);
}

const useStyles = makeStyles({
	tableHeaderTitle: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase200,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase200,
	},
	root: {
		padding: '4px 0',
		boxSizing: 'border-box'
	},
	tableRoot: {
		padding: '0 0px',
	},
	tableHeaderTop: {
		padding: '24px 20px',
		boxSizing: 'border-box',
		display: 'flex',
		justifyContent: 'space-between',
		height: '80px'
	},
	totalRules: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase300,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase300,
		marginRight: '8px'
	},
	tableHeaderRow: {
		padding: '6px 12px',
		boxSizing: 'border-box'
	},
	firstColumnRoot: {
		flex: '4 1 0px',
	},
	firstColumnWrapper: {
		width: '100%',
		display: 'block'
	},
	firstColumnBottom: {
		display: 'block',
		width: '100%',
	},
	firstColumnTitle: {
		color: tokens.colorNeutralForeground1,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase400,
		fontWeight: tokens.fontWeightBold,
		lineHeight: tokens.lineHeightBase400,
	},
	firstColumnLabel: {
		color: tokens.colorNeutralForeground3,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase100,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase100,
	},
	firstColumnValue: {
		color: tokens.colorNeutralForeground2,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase100,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase100,
	},
	authorCellContent: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px'
	},
	authorCellText: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase200,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase200,
	},
	selectedTableRow: {
		backgroundColor: tokens.colorNeutralForeground2BrandSelected
	},
	tableRow: {
		padding: '8px 12px',
		boxSizing: 'border-box',
		':hover': {
			'& button': {
				visibility: 'visible'
			}
		}
	},
});
